import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { UserModelDto } from '@treasury/api/channel';
import { WiresService } from '@treasury/domain/wires';
import { TmBaseComponent } from '@treasury/presentation';
import { SelectItemVm, TmValueChanged } from '@treasury/presentation/components/forms';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { mapCurrencyRangeToDto } from '@treasury/presentation/components/tm-currency-range.mappings';
import '@treasury/presentation/components/tm-date-range';
// eslint-disable-next-line no-duplicate-imports
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { mapDateRangeToDto } from '@treasury/presentation/components/tm-date-range.mappings';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { add } from 'date-fns';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

const defaultReceivedDateRange: DateRange = {
    rangeType: DateRangeType.DateRange,
    startDate: add(new Date(), { weeks: -1 }),
    endDate: new Date(),
};

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultState = {
    uploadDateStart: true,
    uploadDateEnd: true,
    uploadDateType: true,
    uploadedByIds: true,
    wireAmountType: true,
    pagingParameters: true,
};

export const tagName = 'wire-file-filter-sheet';
@customElement(tagName)
export class WireFileFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = true;

    @property({ type: Boolean })
    public open = false;

    @InjectProperty()
    declare private wiresService: WiresService;

    @state()
    private fileName?: string;

    @state()
    private uploadUsers: UserModelDto[] = [];

    @state()
    private uploadUsersSelected: UserModelDto[] = [];

    @state()
    private receivedDateRange: DateRange = defaultReceivedDateRange;

    @state()
    private amountRange: CurrencyRange = defaultAmountRange;

    @DeepReactive()
    private validState = defaultState;

    protected updated(changedProperties: Map<string, unknown>) {
        if (changedProperties.has('loading') && !this.loading) {
            this.setFilterOptions();
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <p slot="header-center">Filter Wire File Activity</p>
            <p slot="header-right"></p>
            <tm-section class="p-3 mb-10">${this.renderForm()}</tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        </tm-bottom-sheet> `;
    }

    private async setFilterOptions() {
        this.uploadUsers = await this.wiresService.fetchWireUploadUsers();
        this.uploadUsersSelected = [...this.uploadUsers];
        this.onApplyFilters();
    }

    private onApplyFilters() {
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: this.toDto(),
            })
        );
        this.dispatchEvent(new CustomEvent('close'));
    }

    private onResetForm() {
        this.fileName = undefined;
        this.uploadUsersSelected = [...this.uploadUsers];
        this.receivedDateRange = { ...defaultReceivedDateRange };
        this.amountRange = { ...defaultAmountRange };
        this.validState = { ...defaultState };
    }

    private toDto() {
        const {
            operator: rangeType,
            dateFrom: uploadDateStart,
            dateTo: dateTo,
        } = mapDateRangeToDto(this.receivedDateRange);
        const uploadDateEnd =
            rangeType === DateRangeType.Today || rangeType === DateRangeType.SpecificDate
                ? uploadDateStart
                : dateTo;

        const {
            operator: wireAmountType,
            fromAmount: wireAmountMin,
            toAmount: wireAmountMax,
            specificAmount: wireAmount,
        } = mapCurrencyRangeToDto(this.amountRange);

        return {
            fileName: this.fileName,
            uploadDateEnd,
            uploadDateStart,
            uploadDateType: 'dateRange',
            uploadedByIds: this.uploadUsersSelected.map(user => user.id),
            wireAmountType,
            wireAmountMin,
            wireAmountMax,
            wireAmount,
        };
    }

    private renderForm() {
        return html`<tm-text-field
                label="File Name"
                .initial=${this.fileName}
                @value-changed=${({ detail }: CustomEvent) => {
                    this.fileName = detail.value;
                }}
            ></tm-text-field>

            <tm-multi-select
                label="Source"
                .allowSelectAll=${true}
                .allSelectedText=${'Select All'}
                .items=${this.uploadUsers}
                .initial=${this.uploadUsersSelected}
                .mapItem=${mapUserToVm}
                @value-changed=${(e: TmValueChanged<UserModelDto[]>) => {
                    this.uploadUsersSelected = e.detail.value || [];
                    this.validState.uploadedByIds = this.uploadUsersSelected.length > 0;
                }}
            ></tm-multi-select>

            <tm-date-range
                label="Received Date"
                .initial=${this.receivedDateRange}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { value } = e.detail;
                    if (value) {
                        this.receivedDateRange = value;
                    }
                }}
            ></tm-date-range>

            <tm-currency-range
                .initial=${this.amountRange}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.wireAmountType = isValid;
                    if (!value) {
                        return;
                    }
                    this.amountRange = value;
                }}
            ></tm-currency-range>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireFileFilterSheet;
    }
}

function mapUserToVm(user: UserModelDto): SelectItemVm<UserModelDto> {
    return {
        label: user.name ?? '',
        value: user,
    };
}
